import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { TelegramActivity, TelegramProcessingStatus } from '@claude-code-insights/shared';
import { fetchActivity } from '../lib/api';

// ─── Status Helpers ──────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<TelegramProcessingStatus, {
  label: string;
  color: string;
  bg: string;
  icon: string;
}> = {
  received: { label: 'Received', color: 'text-obsidian-text-muted', bg: 'bg-obsidian-surface', icon: '•' },
  queued: { label: 'Queued', color: 'text-obsidian-accent', bg: 'bg-obsidian-accent/15', icon: '◌' },
  processing: { label: 'Processing...', color: 'text-obsidian-accent', bg: 'bg-obsidian-accent/15', icon: '◌' },
  published: { label: 'Published', color: 'text-obsidian-success', bg: 'bg-obsidian-success/15', icon: '✓' },
  review: { label: 'Review', color: 'text-obsidian-warning', bg: 'bg-obsidian-warning/15', icon: '◷' },
  discarded: { label: 'Discarded', color: 'text-obsidian-text-faint', bg: 'bg-obsidian-surface', icon: '✕' },
  error: { label: 'Error', color: 'text-obsidian-error', bg: 'bg-obsidian-error/15', icon: '!' },
  command: { label: 'Command', color: 'text-obsidian-text-faint', bg: 'bg-obsidian-surface', icon: '/' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ─── Status Pill ─────────────────────────────────────────────────────────────

function StatusPill({ activity }: { activity: TelegramActivity }) {
  if (activity.processingStatus === 'command') return null;

  const config = STATUS_CONFIG[activity.processingStatus];
  const isAnimated = activity.processingStatus === 'queued' || activity.processingStatus === 'processing';

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${config.bg} ${config.color}`}>
      <span className={isAnimated ? 'animate-pulse' : ''}>{config.icon}</span>
      <span>{config.label}</span>
      {activity.relevanceScore !== null && (
        <span className="opacity-70">· {activity.relevanceScore}/10</span>
      )}
      {activity.aiCategory && (
        <span className="opacity-70">· {activity.aiCategory}</span>
      )}
    </div>
  );
}

// ─── Chat Message Group ──────────────────────────────────────────────────────

function ChatMessageGroup({
  activity,
  isSelected,
  onSelect,
}: {
  activity: TelegramActivity;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      className={`py-3 px-4 cursor-pointer transition-colors rounded-lg ${
        isSelected
          ? 'bg-obsidian-bg-tertiary border-l-2 border-obsidian-accent'
          : 'hover:bg-obsidian-bg-secondary'
      }`}
    >
      {/* User message — right aligned */}
      <div className="flex justify-end mb-2">
        <div className="max-w-[80%]">
          <div className="bg-obsidian-accent/20 border border-obsidian-accent/30 rounded-2xl rounded-br-sm px-3.5 py-2">
            <p className="text-sm text-obsidian-text whitespace-pre-wrap break-words">
              {activity.urls.length > 0 ? (
                <>
                  {activity.urls.map((url, i) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-obsidian-link underline underline-offset-2 break-all"
                    >
                      {url}
                    </a>
                  ))}
                  {activity.userNotes && (
                    <span className="block mt-1 text-obsidian-text-muted">{activity.userNotes}</span>
                  )}
                </>
              ) : (
                activity.messageText
              )}
            </p>
          </div>
          <p className="text-[10px] text-obsidian-text-faint text-right mt-0.5 mr-1">
            {timeAgo(activity.messageDate)}
          </p>
        </div>
      </div>

      {/* Bot reply — left aligned */}
      <div className="flex justify-start mb-2">
        <div className="max-w-[80%]">
          <div className="bg-obsidian-bg-tertiary border border-obsidian-border rounded-2xl rounded-bl-sm px-3.5 py-2">
            <p className="text-sm text-obsidian-text-muted whitespace-pre-wrap">
              {activity.botReply}
            </p>
          </div>
        </div>
      </div>

      {/* Status pill */}
      <div className="flex justify-start ml-1">
        <StatusPill activity={activity} />
      </div>
    </div>
  );
}

// ─── Processing Timeline ─────────────────────────────────────────────────────

interface TimelineStep {
  label: string;
  detail?: string;
  status: 'complete' | 'active' | 'error' | 'pending';
}

function getTimelineSteps(activity: TelegramActivity): TimelineStep[] {
  const s = activity.processingStatus;

  if (s === 'command') {
    return [
      { label: 'Received', detail: 'Bot command', status: 'complete' },
      { label: 'Response sent', detail: activity.botReply.slice(0, 60), status: 'complete' },
    ];
  }

  const steps: TimelineStep[] = [];

  // Step 1: Received
  steps.push({
    label: 'Received',
    detail: `Telegram message at ${new Date(activity.messageDate).toLocaleTimeString()}`,
    status: 'complete',
  });

  // Step 2: Queued
  if (s === 'received') {
    steps.push({ label: 'Queued', status: 'pending' });
    steps.push({ label: 'Content extraction', status: 'pending' });
    steps.push({ label: 'AI analysis', status: 'pending' });
    return steps;
  }

  steps.push({
    label: 'Queued',
    detail: activity.urls.length > 0
      ? `${activity.urls.length} URL${activity.urls.length > 1 ? 's' : ''} sent to processing queue`
      : 'Text sent to processing queue',
    status: 'complete',
  });

  if (s === 'queued') {
    steps.push({ label: 'Content extraction', status: 'pending' });
    steps.push({ label: 'AI analysis', status: 'pending' });
    return steps;
  }

  if (s === 'processing') {
    steps.push({ label: 'Processing', detail: 'Extracting and analyzing content...', status: 'active' });
    steps.push({ label: 'AI analysis', status: 'pending' });
    return steps;
  }

  // Step 3: Content extracted (implied if we got past processing)
  steps.push({
    label: 'Content extracted',
    status: s === 'error' && !activity.aiTitle ? 'error' : 'complete',
    detail: s === 'error' && !activity.aiTitle ? activity.processingError || 'Extraction failed' : undefined,
  });

  if (s === 'error' && !activity.aiTitle) {
    return steps;
  }

  // Step 4: AI analysis
  if (s === 'error' && activity.aiTitle) {
    steps.push({
      label: 'AI analysis',
      status: 'error',
      detail: activity.processingError || 'Analysis failed',
    });
    return steps;
  }

  steps.push({
    label: 'AI analysis',
    detail: `Relevance: ${activity.relevanceScore}/10 · ${activity.aiCategory}`,
    status: 'complete',
  });

  // Step 5: Outcome
  if (s === 'published') {
    steps.push({ label: 'Published', detail: 'Added to knowledge base', status: 'complete' });
  } else if (s === 'review') {
    steps.push({ label: 'Sent to review', detail: 'Needs manual review', status: 'complete' });
  } else if (s === 'discarded') {
    steps.push({ label: 'Discarded', detail: `Relevance too low (${activity.relevanceScore}/10)`, status: 'complete' });
  }

  return steps;
}

function ProcessingTimeline({ activity }: { activity: TelegramActivity }) {
  const steps = getTimelineSteps(activity);

  return (
    <div className="space-y-0">
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        const dotColor = {
          complete: 'bg-obsidian-success',
          active: 'bg-obsidian-accent animate-pulse',
          error: 'bg-obsidian-error',
          pending: 'bg-obsidian-surface',
        }[step.status];

        return (
          <div key={i} className="flex gap-3">
            {/* Timeline line + dot */}
            <div className="flex flex-col items-center">
              <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${dotColor}`} />
              {!isLast && <div className="w-px flex-1 bg-obsidian-border my-1" />}
            </div>
            {/* Content */}
            <div className={`pb-4 ${isLast ? '' : ''}`}>
              <p className={`text-sm font-medium ${
                step.status === 'error' ? 'text-obsidian-error' :
                step.status === 'pending' ? 'text-obsidian-text-faint' :
                'text-obsidian-text'
              }`}>
                {step.label}
              </p>
              {step.detail && (
                <p className="text-xs text-obsidian-text-muted mt-0.5">{step.detail}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── AI Assessment Card ──────────────────────────────────────────────────────

function AiAssessmentCard({ activity }: { activity: TelegramActivity }) {
  if (!activity.aiTitle) return null;

  const scoreColor =
    (activity.relevanceScore ?? 0) >= 8 ? 'text-obsidian-success' :
    (activity.relevanceScore ?? 0) >= 5 ? 'text-obsidian-accent' :
    'text-obsidian-warning';

  return (
    <div className="border-t border-obsidian-border pt-4 mt-4">
      <h4 className="text-xs font-semibold text-obsidian-text-faint uppercase tracking-wider mb-3">
        AI Assessment
      </h4>

      <div className="space-y-3">
        <div>
          <p className="text-sm font-medium text-obsidian-text">{activity.aiTitle}</p>
        </div>

        {activity.aiSummary && (
          <p className="text-xs text-obsidian-text-muted leading-relaxed">{activity.aiSummary}</p>
        )}

        <div className="flex items-center gap-3 text-xs">
          {activity.relevanceScore !== null && (
            <span className={scoreColor}>
              Score: {activity.relevanceScore}/10
            </span>
          )}
          {activity.aiCategory && (
            <span className="badge bg-obsidian-bg-tertiary text-obsidian-text-faint">
              {activity.aiCategory}
            </span>
          )}
        </div>

        {activity.insightIds.length > 0 && (
          <Link
            to={`/insight/${activity.insightIds[0]}`}
            className="inline-flex items-center gap-1 text-xs text-obsidian-link hover:underline"
          >
            View Full Insight →
          </Link>
        )}
      </div>
    </div>
  );
}

// ─── Main View ───────────────────────────────────────────────────────────────

export function TelegramView() {
  const [activities, setActivities] = useState<TelegramActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  const selectedActivity = activities.find((a) => a.activityId === selectedId) || null;
  const hasProcessing = activities.some(
    (a) => a.processingStatus === 'queued' || a.processingStatus === 'processing',
  );

  const loadActivities = useCallback(async (isRefresh = false) => {
    try {
      const result = await fetchActivity({ limit: 50 });
      setActivities(result.items);
      setNextCursor(result.nextCursor);
      // Auto-scroll when new messages arrive
      if (isRefresh && result.items.length > prevCountRef.current) {
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
      prevCountRef.current = result.items.length;
    } catch (err) {
      console.error('Failed to load activity:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await fetchActivity({ limit: 50, cursor: nextCursor });
      setActivities((prev) => [...prev, ...result.items]);
      setNextCursor(result.nextCursor);
    } catch (err) {
      console.error('Failed to load more activity:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(
      () => loadActivities(true),
      hasProcessing ? 3000 : 10000,
    );
    return () => clearInterval(interval);
  }, [autoRefresh, hasProcessing, loadActivities]);

  // Display in chronological order (oldest first) for chat convention
  const chronological = [...activities].reverse();

  return (
    <div className="flex h-full">
      {/* Left panel: Chat transcript */}
      <div className="flex-[55] flex flex-col border-r border-obsidian-border min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-obsidian-border flex-shrink-0">
          <h2 className="text-sm font-semibold">Telegram</h2>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full transition-colors ${
              autoRefresh
                ? 'bg-obsidian-success/15 text-obsidian-success'
                : 'bg-obsidian-surface text-obsidian-text-faint'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-obsidian-success animate-pulse' : 'bg-obsidian-text-faint'}`} />
            Auto-refresh
          </button>
        </div>

        {/* Chat messages */}
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="py-16 text-center text-obsidian-text-muted text-sm">Loading...</div>
          ) : activities.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-obsidian-text-muted text-sm">No messages yet</p>
              <p className="text-obsidian-text-faint text-xs mt-2">
                Send a message to your Telegram bot to see it here
              </p>
            </div>
          ) : (
            <>
              {/* Load older */}
              {nextCursor && (
                <div className="text-center py-2">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="text-xs text-obsidian-text-faint hover:text-obsidian-text transition-colors disabled:opacity-50"
                  >
                    {loadingMore ? 'Loading...' : '↑ Load older messages'}
                  </button>
                </div>
              )}

              {chronological.map((activity) => (
                <ChatMessageGroup
                  key={activity.activityId}
                  activity={activity}
                  isSelected={activity.activityId === selectedId}
                  onSelect={() => setSelectedId(
                    activity.activityId === selectedId ? null : activity.activityId,
                  )}
                />
              ))}
              <div ref={chatEndRef} />
            </>
          )}
        </div>
      </div>

      {/* Right panel: Processing detail */}
      <div className="flex-[45] overflow-y-auto min-w-0">
        {selectedActivity ? (
          <div className="p-5">
            <h3 className="text-xs font-semibold text-obsidian-text-faint uppercase tracking-wider mb-4">
              Processing Timeline
            </h3>
            <ProcessingTimeline activity={selectedActivity} />
            <AiAssessmentCard activity={selectedActivity} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <span className="text-3xl mb-3 opacity-30">✈</span>
            <p className="text-sm text-obsidian-text-muted">Select a message to see</p>
            <p className="text-sm text-obsidian-text-muted">its processing details</p>
          </div>
        )}
      </div>
    </div>
  );
}
